import { BackdropProps, Backdrop as DefaultBackdrop } from '@oy/ui';
import classNames from 'classnames';
import React, {
  ComponentType,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import SideModalBody from '../SideModalBody/SideModalBody';
import { SideModalContext } from '../SideModalContext';
import SideModalFooter from '../SideModalFooter/SideModalFooter';
import SideModalHeader from '../SideModalHeader/SideModalHeader';
import styles from './SideModal.module.css';

export type SideModalProps = {
  Backdrop?: ComponentType<BackdropProps>;
  children: ReactNode;
  closeOnBackdropClick?: boolean;
  onClose?: () => void;
  show: boolean;
  style?: React.CSSProperties;
  isUsingBackdrop?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const SideModal = ({
  Backdrop = DefaultBackdrop,
  children,
  className = '',
  closeOnBackdropClick = false,
  onClose,
  show,
  style = {},
  isUsingBackdrop = true,
  ...props
}: SideModalProps) => {
  const contextValue = { onClose };
  const containerClassName = classNames(styles.container, className, {
    [styles.showModal]: show,
  });

  const onBackdropClick = () => {
    if (closeOnBackdropClick && onClose) {
      onClose();
    }
  };

  const stopEventPropagation = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };

  // Enable mount controls when backdrop is unavailable, preserving animations
  const [mounted, setMounted] = useState(show);

  useEffect(() => {
    if (show) {
      setMounted(true);
    }
  }, [show]);

  const onAnimationEnd = () => {
    if (!show) {
      setMounted(false);
    }
  };

  if (!isUsingBackdrop) {
    if (!mounted) {
      return null;
    }

    return (
      <SideModalContext.Provider value={contextValue}>
        <div
          className={containerClassName}
          style={style}
          onClick={stopEventPropagation}
          onAnimationEnd={onAnimationEnd}
          {...props}
        >
          {children}
        </div>
      </SideModalContext.Provider>
    );
  }

  return (
    <SideModalContext.Provider value={contextValue}>
      <Backdrop show={show} onClick={onBackdropClick}>
        <div
          className={containerClassName}
          style={style}
          onClick={stopEventPropagation}
          {...props}
        >
          {children}
        </div>
      </Backdrop>
    </SideModalContext.Provider>
  );
};

SideModal.Header = SideModalHeader;
SideModal.Body = SideModalBody;
SideModal.Footer = SideModalFooter;

export default SideModal;
